import {
  collection, doc, setDoc, getDocs, getDoc, deleteDoc,
  query, where, addDoc, serverTimestamp, orderBy, onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const generateToken = () =>
  Math.random().toString(36).substr(2, 6) + Math.random().toString(36).substr(2, 6);

// Strip base64 images before saving (Firestore 1MB doc limit)
const prepareBlocks = (blocks) =>
  blocks.map(b =>
    b.type === 'image'
      ? { ...b, imageUrl: b.imageUrl?.startsWith('data:') ? '' : (b.imageUrl || '') }
      : b
  );

// ─── Projects ─────────────────────────────────────────────────────────────────

export const saveProject = async (project) => {
  const { comments: _c, ...rest } = project;
  await setDoc(doc(db, 'projects', project.shareToken), {
    ...rest,
    blocks: prepareBlocks(rest.blocks || []),
    updatedAt: serverTimestamp(),
  });
};

// One-time fetch (HTTP-based, no WebSocket needed)
export const fetchProjects = async (uid) => {
  const q = query(collection(db, 'projects'), where('ownerId', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id }));
};

export const deleteProject = async (shareToken) => {
  await deleteDoc(doc(db, 'projects', shareToken));
};

// One-time fetch for share link
export const fetchProject = async (shareToken) => {
  const snap = await getDoc(doc(db, 'projects', shareToken));
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
};

// ─── Settings ────────────────────────────────────────────────────────────────

export const fetchSettings = async (uid) => {
  const snap = await getDoc(doc(db, 'settings', uid));
  return snap.exists() ? snap.data() : {};
};

export const saveSettings = async (uid, settings) => {
  await setDoc(doc(db, 'settings', uid), settings, { merge: true });
};

// ─── Comments ─────────────────────────────────────────────────────────────────

export const addComment = async (shareToken, comment) => {
  await addDoc(collection(db, 'projects', shareToken, 'comments'), {
    ...comment,
    createdAt: serverTimestamp(),
  });
};

// One-time fetch for comments
export const fetchComments = async (shareToken) => {
  const q = query(
    collection(db, 'projects', shareToken, 'comments'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Real-time listener for comments
export const subscribeComments = (shareToken, callback) => {
  const q = query(
    collection(db, 'projects', shareToken, 'comments'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

// ─── Clients ──────────────────────────────────────────────────────────────────

export const saveClient = async (client) => {
  const ref = client.id
    ? doc(db, 'clients', client.id)
    : doc(collection(db, 'clients'));
  await setDoc(ref, { ...client, id: ref.id, updatedAt: serverTimestamp() }, { merge: true });
  return ref.id;
};

export const fetchClients = async (uid) => {
  const q = query(collection(db, 'clients'), where('ownerId', '==', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id }));
};

export const deleteClient = async (id) => {
  await deleteDoc(doc(db, 'clients', id));
};

export const fetchProjectsByClient = async (uid, clientId) => {
  const q = query(
    collection(db, 'projects'),
    where('ownerId', '==', uid),
    where('clientId', '==', clientId),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id }));
};
